/**
 * The ProximityParks design vocabulary, from Brand Guidelines V2.0.
 *
 * One place, so four components cannot drift into four dialects.
 *
 * WHAT THE BRANDBOOK ACTUALLY SAYS, and what it means for an interface:
 *
 *   "Color application in the layout leans towards the cold and gray colors of
 *    the palette in order to communicate simplicity, luminosity, cleanliness.
 *    However, the use of red ... will be applied to emphasize some layout
 *    elements. So, by being used as an ACCENT, it will have an important
 *    contrast and will stand out."                                     (p.35)
 *
 * So red is a highlighter, never a field. It marks the one thing on a panel
 * that matters most and nothing else. Everything structural is neutral gray.
 *
 * The palette is four colours and no more: #e93529 red, #333333 neutral black,
 * #d8d8d8 gray, #f4f4f4 light gray. Tailwind's `slate` is a BLUE-tinted gray
 * and `sky` is not in the palette at all - both were used in the first cut of
 * these screens and both are off-brand. The ramp below is derived from
 * #333333 so the greys stay genuinely neutral.
 *
 * Typography is Montserrat in all weights. The book sets headlines in light
 * weights with generous tracking and labels in wide-tracked uppercase, which
 * is what `LABEL` and `DISPLAY` reproduce.
 */

// ── the neutral ramp, derived from #333333 and #f4f4f4 ────────────────────
// Named by role rather than by number so a change of mind about a shade does
// not mean renaming it everywhere.
export const ink = {
  page: "#1c1c1c",        // the ground beneath everything
  surface: "#242424",     // a panel
  raised: "#2c2c2c",      // a panel on a panel, or a sticky header
  line: "#3a3a3a",        // hairline rules and borders
  lineSoft: "#303030",    // a rule that should barely register
  muted: "#8a8a8a",       // secondary text
  soft: "#b4b4b4",        // body text
  bright: "#f4f4f4",      // primary text - the brand's own light gray
};

export const red = {
  DEFAULT: "#e93529",
  light: "#f04f44",
  dark: "#c42d22",
  // Tints, for the translucent panels the book uses rather than solid fills.
  wash: "rgba(233,53,41,0.08)",
  edge: "rgba(233,53,41,0.32)",
};

// Semantic colours for figures. Red is reserved for the brand, so a falling
// number uses it only when the point IS that it is bad; gains use a muted
// green that sits quietly beside the neutrals rather than competing.
export const signal = {
  up: "#4f9d76",
  upSoft: "rgba(79,157,118,0.12)",
  down: "#e93529",
  downSoft: "rgba(233,53,41,0.10)",
  warn: "#c8922f",
  warnSoft: "rgba(200,146,47,0.12)",
};

// ── class vocabulary ──────────────────────────────────────────────────────
// Tailwind cannot read the objects above, so the classes carry literal
// arbitrary values. Kept together here for the same reason as the tokens.

export const CARD =
  "bg-[#242424] border border-[#3a3a3a] rounded-lg";

export const CARD_HEAD =
  "px-5 py-3.5 border-b border-[#3a3a3a] flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5";

/** Wide-tracked uppercase, the book's label treatment. */
export const LABEL =
  "text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8a8a8a]";

/** A panel title: Montserrat medium, tight, bright. */
export const TITLE =
  "text-[13px] font-semibold tracking-tight text-[#f4f4f4]";

export const SUBTITLE = "text-[11px] text-[#8a8a8a]";

/** A headline figure. Light weight and open tracking, as the book sets them. */
export const DISPLAY =
  "text-[26px] leading-none font-light tracking-tight tabular-nums text-[#f4f4f4]";

export const TH =
  "px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8a8a8a]";

export const TD = "px-4 py-2 text-[12.5px] text-[#b4b4b4]";

export const ROW_HOVER = "hover:bg-[#2c2c2c]";

export const DIVIDE = "divide-[#303030]";

/** The one accent. Used on the single most important control, not on many. */
export const ACCENT_PILL =
  "bg-[rgba(233,53,41,0.10)] text-[#f04f44] ring-1 ring-[rgba(233,53,41,0.32)]";

export const QUIET_PILL = "bg-[#2c2c2c] text-[#b4b4b4]";

/** Stage colours: neutral by default, red only where a deal is actually dead. */
export const STAGE_TONE = {
  Tracking: "bg-[rgba(138,138,138,0.14)] text-[#8a8a8a]",
  Sourcing: "bg-[rgba(138,138,138,0.14)] text-[#8a8a8a]",
  Outreach: "bg-[rgba(138,138,138,0.14)] text-[#8a8a8a]",
  "Unsolicited Offer": "bg-[rgba(138,138,138,0.14)] text-[#8a8a8a]",
  Screening: "bg-[rgba(138,138,138,0.22)] text-[#b4b4b4]",
  Underwriting: "bg-[rgba(200,146,47,0.16)] text-[#c8922f]",
  "LOI Submitted": "bg-[rgba(233,53,41,0.16)] text-[#f04f44]",
  "In Diligence": "bg-[rgba(79,157,118,0.16)] text-[#7cbb99]",
  Acquired: "bg-[rgba(79,157,118,0.30)] text-[#a9dcc0]",
  Passed: "bg-[rgba(138,138,138,0.12)] text-[#8a8a8a]",
  Lost: "bg-[rgba(233,53,41,0.10)] text-[#e93529]",
  Dead: "bg-[#242424] text-[#7a7a7a]",
};

/**
 * Stage as an ORDINAL ramp for charts on Home: one hue, brightest closest to
 * close, ending on PP Red. Validated against ink.surface (#242424) for monotone
 * lightness, a visible step between neighbours and >= 2:1 at the dim end.
 * The brand red alone as the light step fails that floor, which is why
 * Underwriting is #8c3f38 and not darker. Re-run the check before moving a step.
 */
export const stageRamp = {
  "In Diligence": "#e93529",
  "LOI Submitted": "#bb3a30",
  "Underwriting": "#8c3f38",
};

export const fmtPsf = (v) =>
  Number.isFinite(Number(v)) && Number(v) > 0 ? `$${Number(v).toFixed(2)}` : "—";

export const fmtSf = (v) =>
  Number.isFinite(Number(v)) && Number(v) ? Number(v).toLocaleString() : "—";

export const fmtPct = (v, dp = 1) =>
  Number.isFinite(v) ? `${(v * 100).toFixed(dp)}%` : "—";
